import { Directive, inject, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { AbstractControl } from '@angular/forms';

/**
 * Structural directive that renders its content if a control (or any of controls) exists.
 *
 * Inputs:
 * - `ifControl: AbstractControl | AbstractControl[]` — Control(s) to check for truthy value.
 * - `ifControlCondition: 'or' | 'and'` — Condition strategy, defaults to `or`.
 *
 * Example:
 * ```html
 * <div *ifControl="form.get('email')">Display email control</div>
 * ```
 */
@Directive({ selector: '[ifControl]', standalone: true })
export class IfControlDirective {
    protected viewContainer = inject(ViewContainerRef);

    protected templateRef = inject(TemplateRef);

    protected hasView = false;

    protected _ifControl;

    /**
     * Control or list of controls to evaluate.
     */
    @Input() set ifControl(property: AbstractControl | AbstractControl[]) {
        this._ifControl = property;
        this.calculate();
    }

    protected _ifControlCondition = 'or';

    /**
     * Condition mode: `or` (any truthy) or `and` (all truthy). Defaults to `or`.
     */
    @Input() set ifControlCondition(propertyCondition: any) {
        this._ifControlCondition = propertyCondition;
        this.calculate();
    }

    /**
     * Evaluates condition and creates/clears view accordingly.
     */
    calculate() {
        const props = Array.isArray(this._ifControl) ? this._ifControl : [this._ifControl];
        let show;
        if (this._ifControlCondition === 'or') {
            // show if at least one value is not undefined
            show = props.reduce((acc, current) => acc || current, false);
        } else if (this._ifControlCondition === 'and') {
            // show if all values are not undefined
            show = props.reduce((acc, current) => acc && current, true);
        }

        if (show && !this.hasView) {
            this.viewContainer.createEmbeddedView(this.templateRef);
            this.hasView = true;
        } else if (!show && this.hasView) {
            this.viewContainer.clear();
            this.hasView = false;
        }
    }
}
